<template>
  <v-row class="mx-5">
    <v-col cols="7">
      <div class="p-schedule-visit__content__title justify-start title">
        <span class="heading-4">{{ $t("visits.schedule.second.title") }}</span>
      </div>
      <div class="schedule-block pa-5">
        <span class="heading-5">
          {{ $t("visits.schedule.second.generalMedicalInformation") }}
          <span class="p-schedule-visit__content__required">*</span>
        </span>
        <v-row dense>
          <v-col>
            <BaseInput
              :value="height"
              :placeholder="$t('general.inputs.height')"
              @change="onHeightChange"
              maskPattern="####"
            />
          </v-col>
          <v-col>
            <BaseInput
              :value="weight"
              :placeholder="$t('general.inputs.weight')"
              @change="onWeightChange"
              maskPattern="####"
            />
          </v-col>
        </v-row>
        <v-row class="mx-0 justify-space-between align-center">
          <span class="p-schedule-visit__content__accordion-content__smoking-status mb-0">
            {{ $t("settings.profile.smokingStatus") }}
          </span>
          <v-col cols="4" class="pa-0">
            <Switcher :value="isSmoking" :items="$t('general.smokingStatus')" @change="onIsSmokingChange" />
          </v-col>
        </v-row>
      </div>

      <v-expansion-panels accordion flat>
        <v-expansion-panel
          ref="pmhPanel"
          class="p-schedule-visit__exp-panel p-schedule-visit__exp-panel__2 schedule-block"
        >
          <div class="pa-5">
            <div class="p-schedule-visit__content__accordion-header__column">
              <span class="heading-5">{{ $t("visits.schedule.second.pastMedicalHistory") }}</span>
            </div>
            <div class="d-flex mx-0 mt-4">
              <span class="sub-heading-1 text-left">
                {{ $t("visits.schedule.second.pastMedicalHistorySub") }}
              </span>
              <v-col cols="4" class="pa-0">
                <Switcher :value="hasPMH" :items="$t('general.smokingStatus')" @change="onHasPMHChange" />
              </v-col>
            </div>
          </div>
          <v-expansion-panel-header class="p-schedule-visit__content__accordion-header">
            <span class="p-schedule-visit__content__accordion-header__show">{{
              $t("visits.schedule.second.pastMedicalHistoryShow")
            }}</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="p-schedule-visit__content__accordion-content">
            <PatientHistoryTable
              tableClassName="scrollable-table table__no-border-radius"
              :items="pmh"
              place="checkout"
              id="PMH"
              @change="onPMHChange"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="schedule-block pa-5">
        <div class="p-schedule-visit__content__accordion-header__column">
          <span class="heading-5 mb-4">{{ $t("visits.schedule.second.allergies") }}</span>
        </div>
        <v-row class="mx-0 justify-space-between">
          <span class="sub-heading-1 p-schedule-visit__content__accordion-header__column__sub-header">
            {{ $t("visits.schedule.second.allergiesPrompt") }}
          </span>
          <v-col cols="4" class="pa-0">
            <Switcher v-model="hasAllergies" :items="$t('general.smokingStatus')" />
          </v-col>
        </v-row>
        <div v-if="hasAllergies === 'true'" class="mt-5">
          <p>
            <b>{{ $t("visits.schedule.second.allergiesSub") }}:</b>
          </p>
          <Combobox
            :items="allergies"
            @change="onAllergiesChange"
            :placeholder="$t('visits.schedule.second.allergiesPlaceholder')"
          />
        </div>
      </div>

      <div class="schedule-block">
        <div class="p-schedule-visit__content__accordion-header__column pa-5">
          <span class="heading-5 mb-4">{{ $t("visits.schedule.second.medications") }}</span>
          <v-row class="mx-0 justify-space-between">
            <span
              class="sub-heading-1 p-schedule-visit__content__accordion-header__column__sub-header align-center mt-0"
            >
              {{ $t("visits.schedule.second.medicationsSub") }}
            </span>
            <v-col cols="4" class="pa-0">
              <Switcher v-model="isTakingMedicine" :items="$t('general.smokingStatus')" />
            </v-col>
          </v-row>
        </div>
        <div v-if="isTakingMedicine === 'true'" class="schedule-block__extension">
          <MedicationTable
            tableClassName="scrollable-table table__no-border-radius table__expand-tr"
            :items="medicationsMap"
            place="checkout"
            @change="onMedicationChange"
          />
        </div>
      </div>

      <div class="p-schedule-visit__confirm">
        <v-checkbox
          :value="confirmedAccurace"
          dense
          :label="$t('visits.schedule.second.confirmAccuracy')"
          @change="onConfirmCheckboxChange"
        />
      </div>
      <div class="p-schedule-visit__content__btn-container">
        <PrimaryButton
          className="primary-btn__blue"
          :fullWidth="true"
          :text="$t(`visits.schedule.second.${price ? 'continuePayment' : 'continue'}`)"
          size="large"
          :disabled="buttonDisabled"
          :loading="loading"
          @onClick="submit"
        />
      </div>
    </v-col>
    <v-col cols="5">
      <v-row class="back-wrap">
        <v-icon>mdi-arrow-left</v-icon>
        <span class="back-wrap__text">{{ $t("visits.schedule.second.back") }}</span>
      </v-row>
      <v-card class="back-wrap__card">
        <h2 class="back-wrap__card__title">{{ $t("visits.schedule.steps._1") }}</h2>
        <v-row class="align-center mx-0">
          <Avatar />
          <v-col class="ml-3">
            <v-row class="back-wrap__card__therapist-title">{{ practitioner.qualification }}</v-row>
            <v-row class="back-wrap__card__therapist-name"
              >{{ practitioner.firstName }} {{ practitioner.lastName }}</v-row
            >
          </v-col>
        </v-row>
        <div class="back-wrap__card__details">
          <v-row class="mb-1">
            <img src="@/assets/icons/calendar-blue.svg" />
            <span class="back-wrap__card__details__label">{{ $t("visits.schedule.second.date") }}:</span>
            <span class="back-wrap__card__details__text">{{ date }}</span>
          </v-row>
          <v-row>
            <img src="@/assets/icons/time-blue.svg" />
            <span class="back-wrap__card__details__label">{{ $t("visits.schedule.second.date") }}:</span>
            <span class="back-wrap__card__details__text">{{ timeslot }}</span>
          </v-row>
        </div>
        <v-col class="back-wrap__card__patient-info">
          <v-col>
            <p class="back-wrap__card__patient-info__title">Patient</p>
            <p class="back-wrap__card__patient-info__name">{{ patient.firstName }} {{ patient.lastName }}</p>
            <p class="back-wrap__card__patient-info__email">{{ patient.email }}</p>
          </v-col>
          <v-col>
            <div class="back-wrap__card__patient-info__note">
              <img src="@/assets/icons/rounded-sick.svg" />
              <span class="back-wrap__card__patient-info__note__title">{{ $t("visits.chiefComplaint") }}</span>
            </div>
            <span>{{ chiefComplaint }}</span>
          </v-col>
          <v-col>
            <div class="back-wrap__card__patient-info__note">
              <v-icon size="14" color="primary">mdi-file-outline</v-icon>
              <span class="back-wrap__card__patient-info__note__title">{{ $t("chat.visitReason") }}</span>
            </div>
            <span>{{ complaintDescription }}</span>
          </v-col>
        </v-col>
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import { DateTime } from "luxon";
import { mapGetters, mapMutations } from "vuex";

import Avatar from "@/components/Avatar/Index";
import MedicationTable from "@/components/Medications/Table";
import PatientHistoryTable from "@/components/PatientHistoryTable/Table";
import BaseInput from "@/components/uikit/BaseInput";
import Combobox from "@/components/uikit/Combobox";
import PrimaryButton from "@/components/uikit/PrimaryButton";
import Switcher from "@/components/uikit/Switcher";

export default {
  name: "PatientRequestVisitSecondStep",
  props: ["value", "mode", "price"],
  components: {
    BaseInput,
    Switcher,
    Combobox,
    PrimaryButton,
    MedicationTable,
    PatientHistoryTable,
    Avatar,
  },
  data: () => ({
    loading: false,
    isTakingMedicine: false,
    hasAllergies: false,
    hasPMH: false,
  }),
  computed: {
    ...mapGetters("checkout", [
      "height",
      "weight",
      "isSmoking",
      "allergies",
      "medications",
      "pmh",
      "confirmedAccurace",
      "patientId",
      "chiefComplaint",
      "visitDate",
      "complaintDescription",
      "slotId",
      "reassignPractitionerId",
    ]),
    ...mapGetters("patients", ["patients"]),
    ...mapGetters("slots", ["slotsArray"]),
    ...mapGetters("profileSettings", ["locale"]),
    ...mapGetters("practitioners", ["practitioners"]),
    buttonDisabled() {
      return !this.height || !this.weight || !this.confirmedAccurace;
    },
    medicationsMap() {
      if (!this.medications) {
        return [];
      }
      return this.medications.map((el) => ({
        ...el,
        startDate: new Date(el.startDate),
      }));
    },
    patient() {
      return this.patients.find((p) => p.id === this.patientId);
    },
    timeslot() {
      const slot = this.slotsArray.find((sl) => sl.id === this.slotId);
      const startTime = DateTime.fromISO(slot.start).toFormat("t");
      const endTime = DateTime.fromISO(slot.end).toFormat("t");
      return `${startTime} - ${endTime}`;
    },
    date() {
      return DateTime.fromJSDate(new Date(this.visitDate)).setLocale(this.locale).toLocaleString({
        day: "numeric",
        month: "long",
        weekday: "long",
        year: "numeric",
      });
    },
    practitioner() {
      return this.practitioners.find((p) => p.id === this.reassignPractitionerId);
    },
  },
  methods: {
    ...mapMutations("checkout", [
      "setMedication",
      "setAllergies",
      "setPMH",
      "setIsSmoking",
      "setStringFieldByName",
      "setConfirmedAccurace",
    ]),
    onHeightChange(value) {
      this.setStringFieldByName({ value, fieldName: "height" });
    },
    onWeightChange(value) {
      this.setStringFieldByName({ value, fieldName: "weight" });
    },
    onIsSmokingChange(value) {
      this.setIsSmoking(value);
    },
    onAllergiesChange(value) {
      this.setAllergies(value);
    },
    onMedicationChange(value) {
      this.setMedication(value);
    },
    onPMHChange(value) {
      this.setPMH(value);
      setTimeout(() => this.$forceUpdate(), 0);
    },
    onHasPMHChange(value) {
      if (value === "true" && !this.$refs.pmhPanel.isActive) {
        this.$refs.pmhPanel.toggle();
      } else if (value === "false" && this.$refs.pmhPanel.isActive) {
        this.$refs.pmhPanel.toggle();
      }
      this.hasPMH = value;
    },
    onConfirmCheckboxChange(value) {
      this.setConfirmedAccurace(value);
    },
    async submit() {
      this.$emit("submit");
      this.loading = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.back-wrap {
  display: flex;
  justify-content: flex-start;
  margin-top: 25px;
  margin-left: 0;

  &__text {
    font-size: 16px;
    margin-left: 5px;
  }
  &__card {
    padding: 20px;
    margin-top: 28px;

    &__title {
      font-size: 20px;
      font-weight: normal;
      padding: 10px 0 20px;
      border-bottom: 2px solid $primary;
    }
    &__therapist-title {
      font-size: 15px;
      color: $primaryblack2;
      opacity: 0.4;
    }
    &__therapist-name {
      font-size: 20px;
      color: $primaryblack2;
    }
    &__details {
      background-color: $primarylight1;
      border-radius: 4px;
      padding: 10px;

      & > div {
        margin: 0;
      }
      &__label {
        margin: 0 10px;
        font-size: 16px;
        color: $primaryblack2;
        opacity: 0.6;
      }
      &__text {
        font-size: 16px;
        color: $primaryblack2;
      }
    }

    &__patient-info {
      padding: 0;

      &__title {
        margin: 0;
        color: $primaryblack2;
        opacity: 0.4;
      }
      &__name {
        margin: 0;
        font-size: 20px;
      }
      &__email {
        margin: 0;
        color: $primary;
      }
      &__note {
        display: flex;
        align-items: center;

        &__title {
          margin-left: 5px;
          color: $primaryblack2;
          opacity: 0.4;
          text-transform: capitalize;
        }
      }
    }
  }
}
.schedule-block {
  border: 1px solid $primarylight2;
  border-radius: 6px;
  margin-bottom: 20px;

  &__extension {
    background-color: $greybg;
    opacity: 0.6;
  }

  .v-input__slot {
    border: 1px solid rgba(51, 52, 62, 0.4) !important;
  }
}
.title {
  border-bottom: none;
}
</style>
